import { getCommonFacilityListAsync } from "src/api/building/building-api";
import { getContract_RCA27, getContractApplicationInfo, getQuotationAsync, updateContractApplicationAsync } from "src/api/contract/contract-api";
import { ContractApplicationModel } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { SaveMediaDto } from "src/api/organization/org-types";
import { updateProductAsync } from "src/api/product/product-api";
import { getProductApplicationFormAsync, postProductApplicationAsync } from "src/api/product/product-application-api";
import { ApplicationFormModel, ProductModel } from "src/api/product/product-types";
import { formatDateTimeRange } from "src/utils";

export type ProductApplication = {
  isUseApplication?: boolean;
  formData: ApplicationFormModel;
  attachments?: SaveMediaDto[];
};

export type ApplicationSummary = {
  productInfo: {
    spaceProductId: number | string;
    spaceProductName: string;
    spaceProductType: string;
    facilityName: string;
    useStartEndTime: string;
  };
  contractorInfo: {
    memberNo: string;
    memberEmail: string;
    memberPhoneNumber: string;
    memberName: string;
  };
};

export type UsageApplicationRequest = {
  quotationNo: string;
  contractApplication: ContractApplicationModel;
  contractId?: number;
};

const useUsageApplication = () => {
  // 공간상품 이용신청서 등록
  const { executeAsync: postProductApplication } = useApiOperation(postProductApplicationAsync);
  // 공간상품 이용신청서 조회
  const { executeAsync: getProductApplication } = useApiOperation(getProductApplicationFormAsync);

  // 공간상품 수정 Api
  const { executeAsync: updateProduct } = useApiOperation(updateProductAsync);

  // 견적서 조회
  const { executeAsync: getQuotation } = useApiOperation(getQuotationAsync);

  // 공용공간 조회
  const { executeAsync: getCommonFacility } = useApiOperation(getCommonFacilityListAsync);
  // 계약 이용신청서 조회
  const { executeAsync: getContractApplication } = useApiOperation(getContractApplicationInfo);
  // 계약 이용신청서 수정
  const { executeAsync: updateContractApplication } = useApiOperation(updateContractApplicationAsync);

  // 계약 조회
  const { executeAsync: getContract } = useApiOperation(getContract_RCA27);

  // 계약 이용신청서 조회
  const getContractUsageApplication = async (request: { contractId: number }) => {
    const { data } = await getContractApplication({ contractId: request.contractId });
    return data.data;
  };

  // 계약 이용신청서 수정
  const onUpdateContractApplication = async (request: { contractId: number; formData: ApplicationFormModel }) => {
    const applicationBody = {
      formData: {
        richText: request.formData.richText,
        attachmentConfig: request.formData.attachmentConfig || { isRequired: true },
      },
      attachments:
        request.formData.attachments?.map((attachment) => {
          const { blobUrl, ...attachmentWithoutBlob } = attachment;
          return {
            ...attachmentWithoutBlob,
            serviceType: "CT_APPLICATION",
            serviceTag: "APPLICATION_ATTACHMENT",
            isPrimary: false,
            cmdType: attachment.cmdType || "U",
          };
        }) || ([] as SaveMediaDto[]),
    };

    const { data } = await updateContractApplication({
      contractId: request.contractId,
      applicationBody,
    });
    return data.data;
  };

  // 상품 이용신청서 등록/수정
  const updatePrApplication = async (request: ProductApplication, product?: ProductModel) => {
    if (product) {
      // 2개 API를 병렬로 호출
      const [, applicationResult] = await Promise.all([
        updateProduct({ product: { ...product, isUseApplication: request.isUseApplication } }).catch(() => undefined),
        postProductApplication({ formData: request.formData, productId: product.id.toString() }),
      ]);

      return applicationResult.data.data;
    } else {
      // 1개 API만 호출
      const { data } = await postProductApplication(request);
      return data.data;
    }
  };

  // 상품 이용신청서 조회
  const getPrApplication = async (request: { productId: string }) => {
    const { data } = await getProductApplication({ productId: String(request.productId) });
    return data.data;
  };

  // 이용신청서 모달 오픈
  const getApplicationSummury = async (request: UsageApplicationRequest) => {
    let facilityName = "";
    const { quotationNo, contractApplication, contractId } = request;

    if (quotationNo) {
      const { data } = await getQuotation({ quotationNo });
      const facilityId = data?.data?.content?.[0]?.quotationItemList?.[0]?.buildingCommonFacilityId;

      if (facilityId) {
        const { data } = await getCommonFacility({ buildingCommonFacilityIdList: [facilityId].join(",") });
        facilityName = data.data.content[0].facilityName || "";
      }
    }
    // 계약 목록에서는 quotationNo 가 없어서 계약 조회 후 견적서 조회
    else if (contractId && !quotationNo) {
      const quotationNo = await getContract({ id: contractId }).then((res) => res.data.data.contract?.quotationList?.[0]?.quotationNo);

      const { data } = await getQuotation({ quotationNo: quotationNo || "" });
      const facilityId = data?.data?.content?.[0]?.quotationItemList?.[0]?.buildingCommonFacilityId;

      if (facilityId) {
        const { data } = await getCommonFacility({ buildingCommonFacilityIdList: [facilityId].join(",") });
        facilityName = data.data.content[0].facilityName || "";
      }
    }

    const applicationSummary: ApplicationSummary = {
      productInfo: {
        spaceProductId: contractApplication?.spaceProductId,
        spaceProductName: contractApplication?.spaceProductName,
        spaceProductType: contractApplication?.spaceProductType,
        facilityName: facilityName || "-",
        useStartEndTime: formatDateTimeRange(
          contractApplication?.applicationBody?.useStartTime!,
          contractApplication?.applicationBody?.useEndTime!,
          "S2",
        ),
      },
      contractorInfo: {
        memberNo: contractApplication.memberNo || "-",
        memberEmail: contractApplication.contractorEmail || "-",
        memberPhoneNumber: contractApplication.contractorPhoneNumber || "-",
        memberName: contractApplication.contractorName || "-",
      },
    };

    return applicationSummary;
  };

  return { updatePrApplication, getPrApplication, getApplicationSummury, getContractUsageApplication, onUpdateContractApplication };
};

export default useUsageApplication;
